import {
  annotationToolApiV1,
  getAnnotationToolBasePath,
  type ListResponse,
  type ObjectResponse,
} from '@verantos-dev/core-annotation-tool';

import { METRICS_TAG } from '../constants';
import type {
  AnnotationProgress,
  BatchType,
  BatchwiseAnnotationProgress,
  BatchwiseAnnotationProgressDateRange,
  EncounterStatsDateRange,
  EncounterStatsItem,
  FilterType,
  HolidaysDataItem,
  IRRScoreCardData,
  IRRScoreDataItem,
  IRRScoreDateRange,
  MetricAnnotatorUser,
  MetricsSummaryItem,
  SummaryDateRange,
} from '../types/responseTypes';

export const metricsApiSlice = annotationToolApiV1
  .enhanceEndpoints({
    addTagTypes: [METRICS_TAG],
  })
  .injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
      getListOfAnnotators: builder.query<ListResponse<MetricAnnotatorUser>, { projectId: string }>({
        query: ({ projectId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return `${tempPath}/metrics/list-annotators/${projectId}`;
        },
        providesTags: [METRICS_TAG],
      }),

      getAnnotationProgress: builder.query<
        ObjectResponse<AnnotationProgress>,
        { projectId: string; userId?: string }
      >({
        query: ({ projectId, userId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/metrics/progress/${projectId}`,
            params: userId ? { user_id: userId } : undefined,
          };
        },
        providesTags: [METRICS_TAG],
      }),

      getAnnotationProgressBatchWise: builder.query<
        ListResponse<BatchwiseAnnotationProgress>,
        { projectId: string; userId?: string; startDate?: string; endDate?: string }
      >({
        query: ({ projectId, userId, startDate, endDate }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/metrics/batchwise-progress/${projectId}`,
            params: {
              ...(userId && { user_id: userId }),
              ...(startDate && { start_date: startDate }),
              ...(endDate && { end_date: endDate }),
            },
          };
        },
        providesTags: [METRICS_TAG],
      }),

      getAnnotationProgressBatchWiseDateRange: builder.query<
        ObjectResponse<BatchwiseAnnotationProgressDateRange>,
        { projectId: string; userId?: string }
      >({
        query: ({ projectId, userId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/metrics/batchwise-date-range/${projectId}`,
            params: userId ? { user_id: userId } : undefined,
          };
        },
        providesTags: [METRICS_TAG],
      }),

      getSummaryDateRange: builder.query<
        ObjectResponse<SummaryDateRange>,
        { projectId: string; batchTypeId: string }
      >({
        query: ({ projectId, batchTypeId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/metrics/summary/${projectId}/${batchTypeId}/date-range`,
          };
        },
        providesTags: [METRICS_TAG],
      }),

      getMetricBatchTypeList: builder.query<ListResponse<BatchType>, { projectId: string }>({
        query: ({ projectId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = '/internal-message-gateway' + basePath;
          return {
            url: `${tempPath}/metrics/batch-types`,
            params: { project_id: projectId },
          };
        },
        providesTags: [METRICS_TAG],
      }),

      getMetricsSummary: builder.query<
        ListResponse<MetricsSummaryItem>,
        { projectId: string; batchTypeId: string; startDate?: string; endDate?: string }
      >({
        query: ({ projectId, batchTypeId, startDate, endDate }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/metrics/summary/${projectId}/${batchTypeId}`,
            params: {
              ...(startDate && { start_date: startDate }),
              ...(endDate && { end_date: endDate }),
            },
          };
        },
        providesTags: [METRICS_TAG],
      }),

      emailMetricsSummary: builder.mutation<
        void,
        {
          projectId: string;
          batchTypeId: string;
          startDate?: string;
          endDate?: string;
          emails: string[];
        }
      >({
        query: ({ projectId, batchTypeId, startDate, endDate, emails }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/metrics/summary/${projectId}/${batchTypeId}/email`,
            method: 'POST',
            body: {
              emails,
              ...(startDate && { start_date: startDate }),
              ...(endDate && { end_date: endDate }),
            },
          };
        },
      }),

      getEncounterStatsDateRange: builder.query<
        ObjectResponse<EncounterStatsDateRange>,
        { projectId: string; batchTypeId: string }
      >({
        query: ({ projectId, batchTypeId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/metrics/encounter-statistics/${projectId}/${batchTypeId}/date-range`,
          };
        },
        providesTags: [METRICS_TAG],
      }),

      getMetricsEncounterStats: builder.query<
        ListResponse<EncounterStatsItem>,
        { projectId: string; batchTypeId: string; startDate?: string; endDate?: string }
      >({
        query: ({ projectId, batchTypeId, startDate, endDate }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/metrics/encounter-statistics/${projectId}/${batchTypeId}`,
            params: {
              ...(startDate && { start_date: startDate }),
              ...(endDate && { end_date: endDate }),
            },
          };
        },
        providesTags: [METRICS_TAG],
      }),

      getIRRFilterTypes: builder.query<ListResponse<FilterType>, void>({
        query: () => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = '/internal-message-gateway' + basePath;
          return `${tempPath}/metrics/irr-score/filter-types`;
        },
        providesTags: [METRICS_TAG],
      }),

      getIRRScoreDateRange: builder.query<
        ObjectResponse<IRRScoreDateRange>,
        { projectId: string; batchTypeId: string }
      >({
        query: ({ projectId, batchTypeId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/metrics/irr-score/${projectId}/${batchTypeId}/date-range`,
          };
        },
        providesTags: [METRICS_TAG],
      }),

      getIRRScoreCardsData: builder.query<
        ObjectResponse<IRRScoreCardData>,
        {
          projectId: string;
          batchTypeId: string;
          filterTypeId?: string;
          date?: string;
          annotator1?: string[];
          annotator2?: string[];
          conceptIds?: string[];
          entityIds?: string[];
        }
      >({
        query: ({
          projectId,
          batchTypeId,
          filterTypeId,
          date,
          annotator1,
          annotator2,
          conceptIds,
          entityIds,
        }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          // Use URLSearchParams.append so array values become repeated params
          // (concept_ids=a&concept_ids=b) instead of a comma-joined single value.
          const sp = new URLSearchParams();
          if (filterTypeId) sp.set('filter_type_id', filterTypeId);
          if (date) sp.set('date', date);
          annotator1?.forEach((v) => sp.append('annotator1', v));
          annotator2?.forEach((v) => sp.append('annotator2', v));
          conceptIds?.forEach((v) => sp.append('concept_ids', v));
          entityIds?.forEach((v) => sp.append('entity_ids', v));
          const qs = sp.toString();
          return `${tempPath}/metrics/irr-score/${projectId}/${batchTypeId}/cards${
            qs ? `?${qs}` : ''
          }`;
        },
        providesTags: [METRICS_TAG],
      }),

      getIRRScoreData: builder.query<
        ListResponse<IRRScoreDataItem>,
        {
          projectId: string;
          batchTypeId: string;
          filterTypeId?: string;
          date?: string;
          annotator1?: string[] | null;
          annotator2?: string[] | null;
          conceptIds?: string[] | null;
          entityIds?: string[] | null;
        }
      >({
        query: ({
          projectId,
          batchTypeId,
          filterTypeId,
          date,
          annotator1,
          annotator2,
          conceptIds,
          entityIds,
        }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/metrics/irr-score/${projectId}/${batchTypeId}`,
            params: {
              ...(filterTypeId && { filter_type_id: filterTypeId }),
              ...(date && { date: date }),
              ...(annotator1?.length && { annotator1 }),
              ...(annotator2?.length && { annotator2 }),
              ...(conceptIds?.length && { concept_ids: conceptIds }),
              ...(entityIds?.length && { entity_ids: entityIds }),
            },
          };
        },
        providesTags: [METRICS_TAG],
      }),

      getHolidaysData: builder.query<ListResponse<HolidaysDataItem>, void>({
        query: () => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/metrics/holidays`,
          };
        },
        providesTags: [METRICS_TAG],
      }),
    }),
  });

export const {
  useGetListOfAnnotatorsQuery,
  useGetAnnotationProgressQuery,
  useGetAnnotationProgressBatchWiseQuery,
  useGetAnnotationProgressBatchWiseDateRangeQuery,
  useGetSummaryDateRangeQuery,
  useGetMetricBatchTypeListQuery,
  useGetMetricsSummaryQuery,
  useEmailMetricsSummaryMutation,
  useGetEncounterStatsDateRangeQuery,
  useGetMetricsEncounterStatsQuery,
  useGetIRRFilterTypesQuery,
  useGetIRRScoreDateRangeQuery,
  useGetIRRScoreCardsDataQuery,
  useGetIRRScoreDataQuery,
  useGetHolidaysDataQuery,
} = metricsApiSlice;
